import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { IntlProvider } from 'react-intl';
import { AUTH_QUERY, SET_PREFERRED_LOCALE_MUTATION } from '../gql';
import { Locale, defaultLocale, normalizeLocale } from './locale';
import { messages } from './messages';

interface LocaleContextValue {
  locale: Locale;
  changeLocale: (locale: Locale) => Promise<void>;
  changingLocale: boolean;
  localeError: Error | null;
}

interface LocaleProviderProps extends React.PropsWithChildren<{}> {
  initialLocale?: Locale;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function updateDocumentLocale(locale: Locale): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

export function LocaleProvider({
  children,
  initialLocale = defaultLocale,
}: LocaleProviderProps) {
  const [locale, setLocale] = useState<Locale>(initialLocale);
  const [localeError, setLocaleError] = useState<Error | null>(null);
  const { data } = useQuery(AUTH_QUERY, { fetchPolicy: 'cache-first' });
  const [
    persistLocale,
    { loading: changingLocale },
  ] = useMutation(SET_PREFERRED_LOCALE_MUTATION, {
    refetchQueries: [{ query: AUTH_QUERY }],
  });

  const preferredLocale = normalizeLocale(data?.viewer?.user?.preferredLocale);

  useEffect(() => {
    if (preferredLocale) {
      setLocale(preferredLocale);
      updateDocumentLocale(preferredLocale);
    }
  }, [preferredLocale]);

  const changeLocale = useCallback(
    async (nextLocale: Locale) => {
      if (nextLocale === locale) {
        return;
      }

      const previousLocale = locale;
      setLocaleError(null);
      setLocale(nextLocale);
      updateDocumentLocale(nextLocale);

      try {
        await persistLocale({ variables: { locale: nextLocale } });
      } catch (error) {
        setLocale(previousLocale);
        updateDocumentLocale(previousLocale);
        setLocaleError(
          error instanceof Error ? error : new Error('Could not save locale')
        );
      }
    },
    [locale, persistLocale]
  );

  const contextValue = useMemo(
    () => ({ locale, changeLocale, changingLocale, localeError }),
    [changeLocale, changingLocale, locale, localeError]
  );

  return (
    <LocaleContext.Provider value={contextValue}>
      <IntlProvider
        defaultLocale={defaultLocale}
        locale={locale}
        messages={messages[locale]}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);

  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider');
  }

  return context;
}
